import { ButtonHTMLAttributes, useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  COLOR_PRIMARY,
  COLOR_LIGHT_BLUE,
} from "../../constants/color";
import { IconComponent } from "./icons/types";

export type ButtonVariant = "filled" | "border" | "dark" | "link";

type Props = {
  uuid?: string;
  text: string;
  variant: ButtonVariant;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: IconComponent;
  isSubmitting?: boolean; // Custom submission state to prevent double clicks
};

export const Button: React.FC<Props> = ({
  uuid,
  text,
  variant,
  type,
  onClick,
  disabled,
  fullWidth,
  icon: Icon,
  isSubmitting,
}) => {
  const formContext = useFormContext();
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const handleOnClick = useCallback((e?: React.MouseEvent) => {
    if (disabled || isSubmitting) {
      e?.preventDefault();
      return;
    }

    onClick?.();
  }, [disabled, isSubmitting, onClick]);



  const iconColor = useMemo(() => {
    switch (variant) {
      case "filled":
        return "#ffffff";
      case "border":
        return isHovered ? COLOR_LIGHT_BLUE : COLOR_PRIMARY;
      case "dark":
        return isHovered ? COLOR_PRIMARY : "#000000";
      case "link":
        return isHovered ? COLOR_LIGHT_BLUE : COLOR_PRIMARY;
      default:
        return "#333333";
    }
  }, [variant, isHovered]);

  return (
    <StyledButton
      id={uuid ? `button-${uuid}` : undefined}
      $variant={variant}
      type={type}
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={
        type === "submit"
          ? formContext
            ? (!formContext.formState.isValid || formContext.formState.isSubmitting || isSubmitting)
            : (disabled || isSubmitting)
          : (disabled || isSubmitting)
      }
      $fullWidth={fullWidth}
    >
      {Icon && <Icon fillColor={iconColor} />} {text}
    </StyledButton>
  );
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $fullWidth?: boolean;
}>`
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
  min-width: 100px;
  height: ${({ $variant }) => ($variant === "link" ? "40px" : "48px")};
  padding: ${({ $variant }) =>
    $variant === "link" ? "10px 14px" : "14px 24px"};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  text-align: center;
  font-size: 14px;
  font-weight: ${({ $variant }) =>
    $variant === "filled" || $variant === "border" ? "700" : "400"};
  line-height: 100%;
  cursor: pointer;
  transition: background-color 0.3s;

  @media (max-width: 768px) {
    min-width: 80px;
    height: ${({ $variant }) => ($variant === "link" ? "36px" : "44px")};
    padding: ${({ $variant }) =>
    $variant === "link" ? "8px 12px" : "12px 20px"};
    border-radius: 6px;
    font-size: 13px;
    gap: 3px;
  }

  @media (max-width: 480px) {
    min-width: 70px;
    height: ${({ $variant }) => ($variant === "link" ? "32px" : "40px")};
    padding: ${({ $variant }) =>
    $variant === "link" ? "6px 10px" : "10px 16px"};
    border-radius: 4px;
    font-size: 12px;
    gap: 2px;
  }
  ${({ $variant }) => {
    switch ($variant) {
      case "filled":
        return `
          background-color: ${COLOR_PRIMARY};
          color: #ffffff;
          border: none;
        `;
      case "border":
        return `
          background-color: #ffffff;
          color: ${COLOR_PRIMARY};
          border: 1px solid ${COLOR_PRIMARY};
        `;
      case "dark":
        return `
            background-color: #ffffff;
            color: #000000;
            border: 1px solid #999999;
          `;
      case "link":
        return `
          background-color: transparent;
          color: ${COLOR_PRIMARY};
          border: none;
          text-decoration: underline;
        `;
    }
  }}

  &:hover {
    ${({ $variant }) => {
    switch ($variant) {
      case "filled":
        return `
            @media (min-width: 990px) {
              background-color: ${COLOR_LIGHT_BLUE};
            }
          `;
      case "border":
        return `
            @media (min-width: 990px) {
              background-color: #ffffff;
              color: ${COLOR_LIGHT_BLUE};
              border-color: ${COLOR_LIGHT_BLUE};
            }
          `;
      case "dark":
        return `
            @media (min-width: 990px) {
              background-color: #ffffff;
              color: ${COLOR_PRIMARY};
              border-color: ${COLOR_PRIMARY};
            }
          `;
      case "link":
        return `
            @media (min-width: 990px) {
              color: ${COLOR_LIGHT_BLUE};
              text-decoration: none;
            }
          `;
    }
  }};
  }

  &:disabled {
    cursor: not-allowed;
    ${({ $variant }) => {
    switch ($variant) {
      case "filled":
        return `
            background-color: #cccccc;
            border-color: #999999;
            color: #f2f2f2;
          `;
      case "border":
      case "dark":
        return `
            background-color: #f2f2f2;
            border-color: #cccccc;
            color: #999999;
          `;
      case "link":
        return `
            color: #999999;
            text-decoration: none;
          `;
    }
  }}
  }
`;
